import { createAsyncThunk } from '@reduxjs/toolkit';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit/src/createAction';

import { showError } from '@/app/actions';
import { eventTracking } from '@/services/event-tracking';
import { ThunkResult } from '@/shared/actions/thunk-result';

import { TSelectHolderId, TSelectIsBusy, TUploadFile } from './internal-types';
import { IDocUploaderForm } from './types';

interface IParams<TDocType extends string> {
  namespace: string;
  eventTrackingType: string;
  selectHolderId: TSelectHolderId;
  selectIsBusy: TSelectIsBusy;
  actionSetBusy: ActionCreatorWithPayload<boolean, string>;
  uploadFile: TUploadFile<TDocType>;
}

interface ISubmitFormsActionParams<TDocType extends string> {
  forms: IDocUploaderForm<TDocType>[];
  textDefaultError: string;
}

interface ISubmitFormsResult<TDocType extends string> {
  uploadedDocTypes: TDocType[];
  failedDocType?: TDocType;
  errorMessage?: string;
}

const createAsyncActions = <TDocType extends string>({
  namespace,
  eventTrackingType,
  selectHolderId,
  actionSetBusy,
  uploadFile,
  selectIsBusy,
}: IParams<TDocType>) => {
  const submitUploadForms = createAsyncThunk<
    ThunkResult<ISubmitFormsResult<TDocType>>,
    ISubmitFormsActionParams<TDocType>
  >(
    `${namespace}/submit-upload-forms`,
    async ({ forms, textDefaultError }, { getState, dispatch }) => {
      if (selectIsBusy(getState())) {
        return { isAlreadyExecuted: true };
      }

      const uploadedDocTypes: TDocType[] = [];

      try {
        dispatch(actionSetBusy(true));

        const holderId = selectHolderId(getState());

        if (typeof holderId !== 'string' || holderId === '') {
          throw new Error("can't upload: missing holder id");
        }

        for (const form of forms) {
          const result = await uploadFile(form, holderId).catch(() => ({
            error: textDefaultError,
          }));

          if ('error' in result) {
            const errorMessage = result.error || textDefaultError;

            dispatch(showError(errorMessage));

            return {
              isSucceed: false,
              result: { uploadedDocTypes, failedDocType: form.docType, errorMessage },
            };
          }

          uploadedDocTypes.push(form.docType);

          if (eventTrackingType) {
            eventTracking.trackSnowplow({
              event: 'Onboarding - Documents - Document - Success',
              type: eventTrackingType,
              docType: form.docType,
            });
          }
        }

        return { isSucceed: true, result: { uploadedDocTypes } };
      } finally {
        dispatch(actionSetBusy(false));
      }
    },
  );

  return {
    submitUploadForms,
  };
};

export type { ISubmitFormsActionParams, ISubmitFormsResult };
export { createAsyncActions };
